import React, { useEffect, useState, useRef } from 'react';
import { PlayIcon, ExternalLinkIcon, ChevronRightIcon } from 'lucide-react';
export const StoriesSection = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [showScrollHint, setShowScrollHint] = useState(true);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    }, {
      threshold: 0.15
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;
    const handleScroll = () => {
      const rect = scrollContainer.getBoundingClientRect();
      const isInView = rect.top < window.innerHeight && rect.bottom > 0;
      if (isInView && scrollContainer.scrollLeft === 0) {
        scrollContainer.scrollTo({
          left: 50,
          behavior: 'smooth'
        });
      }
      // Hide scroll hint after user scrolls
      if (scrollContainer.scrollLeft > 50) {
        setShowScrollHint(false);
      }
    };
    const handleContainerScroll = () => {
      if (scrollContainer.scrollLeft > 50) {
        setShowScrollHint(false);
      }
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    scrollContainer.addEventListener('scroll', handleContainerScroll, {
      passive: true
    });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      scrollContainer.removeEventListener('scroll', handleContainerScroll);
    };
  }, []);
  return <section ref={sectionRef} className="bg-[#fcfcf7] py-12 md:py-16 overflow-hidden" data-id="element-234">
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-6 md:mb-8" data-id="element-235">
        <div className={`flex flex-col items-start text-left transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-236">
          <h2 className="text-primary text-2xl md:text-3xl lg:text-5xl font-light leading-tight tracking-tight max-w-lg" data-id="element-237">
            Stories from scientists, innovators, and members like you.
          </h2>
        </div>
      </div>
      {/* Horizontal Scrolling Gallery */}
      <div className="relative" data-id="element-238">
        {/* Fade indicators on edges */}
        <div className="absolute left-0 top-0 bottom-0 w-12 md:w-24 bg-gradient-to-r from-[#fcfcf7] to-transparent z-10 pointer-events-none" data-id="element-239"></div>
        <div className="absolute right-0 top-0 bottom-0 w-12 md:w-24 bg-gradient-to-l from-[#fcfcf7] to-transparent z-10 pointer-events-none" data-id="element-240"></div>
        {/* Animated scroll indicator - Mobile only */}
        {showScrollHint && <div className="absolute right-4 top-1/2 -translate-y-1/2 z-20 md:hidden pointer-events-none" data-id="element-241">
            <div className="flex items-center gap-1 animate-bounce" data-id="element-242">
              <ChevronRightIcon className="w-6 h-6 text-primary/40" data-id="element-243" />
              <ChevronRightIcon className="w-6 h-6 text-primary/60 -ml-3" data-id="element-244" />
              <ChevronRightIcon className="w-6 h-6 text-primary/80 -ml-3" data-id="element-245" />
            </div>
          </div>}
        <div ref={scrollRef} className="overflow-x-auto scrollbar-hide px-6 md:px-12 snap-x snap-mandatory" style={{
        scrollbarWidth: 'none',
        WebkitOverflowScrolling: 'touch'
      }} data-id="element-246">
          <div className="inline-grid grid-flow-col auto-cols-max gap-3 md:gap-4 grid-rows-2" data-id="element-247">
            {/* Large Circular Image - spans 2 rows */}
            <div className={`row-span-2 w-[280px] h-[280px] md:w-[430px] md:h-[430px] rounded-full overflow-hidden relative group snap-start transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`} style={{
            transitionDelay: '100ms'
          }} data-id="element-248">
              <img src="https://cdn.shopify.com/s/files/1/0637/8940/0115/files/bb4aaa1f-5bfa-4174-93b3-0c5576c8974d.png?v=1763926873" alt="F&F Product" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-249" />
            </div>
            {/* Vertical Image - spans 2 rows */}
            <div className={`row-span-2 w-[180px] h-[280px] md:w-[276px] md:h-[429px] rounded-2xl overflow-hidden relative group snap-start transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`} style={{
            transitionDelay: '200ms'
          }} data-id="element-250">
              <img src="https://cdn.shopify.com/s/files/1/0637/8940/0115/files/Gemini_Generated_Image_5p98y85p98y85p98.png?v=1763928093" alt="F&F Supplement" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-251" />
            </div>
            {/* Vertical Video - spans 2 rows */}
            <div className={`row-span-2 w-[180px] h-[280px] md:w-[276px] md:h-[429px] rounded-2xl overflow-hidden relative group snap-start transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`} style={{
            transitionDelay: '300ms'
          }} data-id="element-252">
              <video src="https://cdn.shopify.com/s/files/1/0637/8940/0115/files/sdasfaf.mkv?v=1763924133" className="w-full h-full object-cover" loop muted playsInline data-id="element-253" />
              <button className="absolute top-3 right-3 md:top-4 md:right-4 bg-black/30 backdrop-blur-sm text-white rounded-full p-3 md:p-4 hover:bg-black/50 transition-colors" data-id="element-254">
                <PlayIcon className="w-4 h-4 md:w-5 md:h-5" data-id="element-255" />
              </button>
            </div>
            {/* Horizontal Pill Image */}
            <div className={`w-[210px] h-[135px] md:w-[322px] md:h-[207px] rounded-full overflow-hidden relative group snap-start transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`} style={{
            transitionDelay: '400ms'
          }} data-id="element-256">
              <img src="https://cdn.shopify.com/s/files/1/0637/8940/0115/files/867c10f0-376f-4aab-afa3-eb7942e80ea8.png?v=1763934940" alt="F&F Product Detail" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-257" />
            </div>
            {/* Article Link Card */}
            <a href="#" target="_blank" className={`w-[210px] h-[135px] md:w-[322px] md:h-[207px] rounded-2xl overflow-hidden relative group bg-beige flex items-center justify-center snap-start transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`} style={{
            transitionDelay: '500ms'
          }} data-id="element-258">
              <button className="absolute top-3 right-3 md:top-4 md:right-4 bg-primary/80 backdrop-blur-sm text-white rounded-full p-2 md:p-3 hover:bg-primary transition-colors z-10" data-id="element-259">
                <ExternalLinkIcon className="w-3 h-3 md:w-4 md:h-4" data-id="element-260" />
              </button>
              <div className="text-center px-4 md:px-8" data-id="element-261">
                <p className="text-primary text-sm md:text-lg font-light italic" data-id="element-262">
                  "Biblical botanicals meet modern science"
                </p>
              </div>
            </a>
            {/* Vertical Image */}
            <div className={`row-span-2 w-[180px] h-[280px] md:w-[276px] md:h-[429px] rounded-2xl overflow-hidden relative group snap-start transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`} style={{
            transitionDelay: '600ms'
          }} data-id="element-263">
              <img src="https://cdn.shopify.com/s/files/1/0637/8940/0115/files/72532349-ff6e-47c4-9330-04556b782119.png?v=1763794573" alt="F&F Bottle" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-264" />
            </div>
            {/* Vertical Image */}
            <div className={`row-span-2 w-[180px] h-[280px] md:w-[276px] md:h-[429px] rounded-2xl overflow-hidden relative group snap-start transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`} style={{
            transitionDelay: '700ms'
          }} data-id="element-265">
              <img src="https://cdn.shopify.com/s/files/1/0637/8940/0115/files/d8c379ca-5600-45f5-9690-97da2d6011ee.png?v=1763794573" alt="F&F Product" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-266" />
            </div>
            {/* Article Link Card */}
            <a href="#" target="_blank" className={`w-[210px] h-[135px] md:w-[322px] md:h-[207px] rounded-2xl overflow-hidden relative group bg-cream flex items-center justify-center snap-start transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`} style={{
            transitionDelay: '800ms'
          }} data-id="element-267">
              <button className="absolute top-3 right-3 md:top-4 md:right-4 bg-primary/80 backdrop-blur-sm text-white rounded-full p-2 md:p-3 hover:bg-primary transition-colors z-10" data-id="element-268">
                <ExternalLinkIcon className="w-3 h-3 md:w-4 md:h-4" data-id="element-269" />
              </button>
              <div className="text-center px-4 md:px-8" data-id="element-270">
                <p className="text-primary text-sm md:text-lg font-light italic" data-id="element-271">
                  "Supporting your faith journey"
                </p>
              </div>
            </a>
            {/* Horizontal Pill Image */}
            <div className={`w-[210px] h-[135px] md:w-[322px] md:h-[207px] rounded-full overflow-hidden relative group bg-primary/10 snap-start transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`} style={{
            transitionDelay: '900ms'
          }} data-id="element-272">
              <img src="https://cdn.shopify.com/s/files/1/0637/8940/0115/files/843ed722-fcab-49c4-9afc-c384ca51f27b.png?v=1763794574" alt="F&F Supplement" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-273" />
            </div>
          </div>
        </div>
      </div>
      {/* Scroll hint - Mobile only */}
      <div className={`text-center mt-4 md:hidden transition-all duration-500 ${showScrollHint ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} data-id="element-274">
        <p className="text-primary/60 text-xs font-light animate-pulse" data-id="element-275">
          ← Swipe to explore more →
        </p>
      </div>
    </section>;
};